/**
 * Templates (Glossary Blueprints) Data Source
 */

import type { 
  DataSourceModule, 
  FieldMetadata, 
  ColumnDefinition, 
  ListWidgetItem,
  TableDataOutput,
  TableItemsOutput,
  TableDataRow,
  DataSourceConfig,
  TransformerContext
} from '../types';
import { getCachedTemplates, getCachedTemplatesByFolder } from "@/lib/stores/templatesStore";
import { getPendingChangeById } from "@/lib/stores/pendingChangesStore";
import { GlossaryBlueprint } from "@/types-games";
import { formatDate } from '../shared/formatters';
import { CardData } from '@/components/layout/element-layout/grid-layout/GridLayout';
import { formatRelativeTime, convertTagIdsToNames } from "@/lib/utils";
import { formatVersion } from "@/lib/utils/versionUtils";
import { getStatusColor } from "@/lib/utils/statusColors";

// Field definitions with metadata
const fields: Record<string, FieldMetadata> = {
  id: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  name: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: true,
    required: true,
  },
  description: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: false,
  },
  owner: {
    type: 'string',
    editable: 'dropdown',
    filterable: true,
    sortable: true,
    linkedDataSource: 'users',
    displayField: 'username',
    valueField: 'id',
  },
  tags: {
    type: 'array',
    editable: 'dropdown',
    filterable: true,
    sortable: false,
    linkedDataSource: 'tags',
    multiSelect: true,
    displayField: 'name',
    valueField: 'id',
  },
  status: {
    type: 'string',
    editable: 'dropdown',
    filterable: true,
    sortable: true,
    linkedDataSource: 'statuses',
  },
  folder: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  type: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: true,
  },
  created_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  },
  updated_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  },
  created_by: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
    linkedDataSource: 'users',
    displayField: 'username',
    valueField: 'id',
  },
  updated_by: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
    linkedDataSource: 'users',
    displayField: 'username',
    valueField: 'id',
  }
};

// Column definitions
const columns: ColumnDefinition[] = [
  { key: 'name', label: 'Name', filterable: true, metadata: fields.name },
  { key: 'description', label: 'Description', filterable: true, metadata: fields.description },
  { key: 'owner', label: 'Owner', filterable: true, metadata: fields.owner },
  { key: 'tags', label: 'Tags', filterable: true, metadata: fields.tags },
  { key: 'updated_at', label: 'Last Update', align: 'left', metadata: fields.updated_at },
];

/**
 * Column keys for templates table view (matching the UI expectations)
 */
const TABLE_COLUMNS = ["Name", "Description", "Owner", "Tags", "Last Update"];

// Configuration
const config: DataSourceConfig = {
  type: 'templates',
  title: 'Templates',
  description: 'Templates for objects',
  expandable: false,
};

// Data fetcher - merges pending changes with cached data
function fetchData(selectedGame?: any, selectedFolderId?: string | null): GlossaryBlueprint[] {
  if (!selectedGame?.id) return [];
  
  // Get base data from cache
  let baseData: GlossaryBlueprint[];
  if (selectedFolderId !== undefined && selectedFolderId !== null) {
    baseData = getCachedTemplatesByFolder(selectedGame.id, selectedFolderId);
  } else {
    baseData = getCachedTemplates(selectedGame.id);
  }
  
  // Merge pending changes into the data
  // This ensures that local edits are immediately visible everywhere
  return baseData.map(item => {
    const pendingChange = getPendingChangeById(item.id);
    if (pendingChange && pendingChange.pendingData) {
      // Use pending data instead of cached data for items with local changes
      return {
        ...item,
        ...pendingChange.pendingData,
        // Ensure we keep the ID and game_id from the original
        id: item.id,
        game_id: item.game_id,
      };
    }
    return item;
  });
}

// Transformers
function toList(rawData: GlossaryBlueprint[]): ListWidgetItem[] {
  return rawData.map(blueprint => ({
    id: blueprint.id,
    name: blueprint.name,
    tags: 'Template',
    type: 'Glossary Template',
    created_at: blueprint.created_at,
    description: blueprint.description || 'No description',
    data: blueprint.data || {},
    settings: blueprint.settings || {},
    updated_at: blueprint.updated_at,
    created_by: blueprint.created_by,
    updated_by: blueprint.updated_by,
  }));
}

function toTable(rawData: GlossaryBlueprint[]): TableDataOutput {
  return {
    columns,
    data: rawData.map(blueprint => ({
      id: blueprint.id,
      name: String(blueprint.name),
      type: String(blueprint.type || 'Template'),
      description: String(blueprint.description || 'No description'),
      created_at: formatDate(blueprint.created_at),
      updated_at: formatDate(blueprint.updated_at),
      created_by: String(blueprint.created_by || 'Unknown'),
      updated_by: String(blueprint.updated_by || 'Unknown'),
    })),
    metadata: {
      totalCount: rawData.length,
      filteredCount: rawData.length
    }
  };
}

/**
 * Transform raw data to CardData format
 * @param rawData - Array of GlossaryBlueprint from store
 * @param context - Optional context with tags, getUserNameById for resolving IDs
 */
function toCard(rawData: GlossaryBlueprint[], context?: TransformerContext): CardData[] {
  const { tags = [], getUserNameById } = context || {};
  
  return rawData.map(blueprint => {
    const settings = blueprint.settings || {};
    const color = settings.color as string;
    const statusColor = getStatusColor(blueprint.status, 'templates', blueprint.game_id);
    
    // Resolve owner name
    const ownerName = blueprint.owner && getUserNameById 
      ? getUserNameById(blueprint.owner) 
      : (blueprint.owner || 'Unknown');
    
    // Resolve creator name
    const creatorName = blueprint.created_by && getUserNameById 
      ? getUserNameById(blueprint.created_by) 
      : (blueprint.created_by || 'Unknown');
    
    // Resolve tag names
    const tagNames = tags.length > 0 
      ? convertTagIdsToNames(blueprint.tags, tags) 
      : (Array.isArray(blueprint.tags) ? blueprint.tags : []);
    
    // Format version
    let versionString = '0.000.001';
    if (blueprint.version) {
      if ('major' in blueprint.version) {
        versionString = formatVersion(blueprint.version);
      } else if ('version' in blueprint.version) {
        versionString = String(blueprint.version.version || 0);
      }
    }

    return {
      id: blueprint.id,
      gameId: blueprint.game_id,
      title: blueprint.name,
      owner: ownerName,
      ownerId: blueprint.owner || undefined,
      editPermissions: blueprint.edit_permissions || 'Open',
      creator: creatorName,
      tags: tagNames,
      description: blueprint.description || "No description available",
      lastUpdate: formatRelativeTime(blueprint.updated_at),
      version: versionString,
      icon: settings.icon as string,
      iconBg: color,
      shadowType: color ? "template" as const : (blueprint.status ? "status" as const : "black" as const),
      shadowColor: color || statusColor,
      statusColor: statusColor,
      hasVersionMismatch: false,
      status: blueprint.status,
      folder: blueprint.folder,
      itemType: 'templates',
    };
  });
}

/**
 * Transform raw data to TableItem[] format for MainTableWidget
 * @param rawData - Array of GlossaryBlueprint from store
 * @param context - Optional context with currentUserId, tags lookup
 */
function toTableItems(rawData: GlossaryBlueprint[], context?: TransformerContext): TableItemsOutput {
  const { tags = [], getUserNameById, currentUserId } = context || {};
  
  const items: TableDataRow[] = rawData.map(blueprint => {
    // Determine lock status
    const ownerId = blueprint.owner;
    const editPermissions = blueprint.edit_permissions || 'Open';
    const isLocked = editPermissions === 'Locked' && 
                     ownerId !== undefined && 
                     currentUserId !== undefined && 
                     ownerId !== currentUserId;
    
    // Resolve owner name
    const ownerName = blueprint.owner && getUserNameById 
      ? getUserNameById(blueprint.owner) 
      : (blueprint.owner || 'Unknown');
    
    // Resolve tag names  
    const tagNames = tags.length > 0 
      ? convertTagIdsToNames(blueprint.tags, tags) 
      : (Array.isArray(blueprint.tags) ? blueprint.tags : []);
    
    return {
      type: "row" as const,
      key: blueprint.id,
      depth: 0,
      isLocked,
      ownerId,
      editPermissions: editPermissions as 'Open' | 'Locked',
      content: TABLE_COLUMNS.map((columnKey, index) => {
        const normalizedKey = columnKey.toLowerCase().replace(/\s+/g, "");
        let content: string | boolean | number = "";

        switch (normalizedKey) {
          case "name":
            content = blueprint.name || "";
            break;
          case "description":
            content = blueprint.description || "";
            break;
          case "owner":
            content = ownerName;
            break;
          case "tags":
            content = tagNames.join(", ");
            break;
          case "lastupdate":
            content = formatRelativeTime(blueprint.updated_at);
            break;
          default:
            content = "";
        }

        return {
          key: columnKey,
          fixedX: index === 0,
          content,
        };
      }),
    };
  });

  return {
    items,
    columns: TABLE_COLUMNS,
    fieldMetadata: fields,
  };
}

// Export the module
export const templatesDataSource: DataSourceModule = {
  type: 'templates',
  config,
  fields,
  columns,
  fetchData,
  transformers: {
    toList,
    toTable,
    toCard,
    toTableItems
  }
};

// Export table columns for use in components
export { TABLE_COLUMNS as TEMPLATES_TABLE_COLUMNS };

